import { useQueryClient } from '@tanstack/react-query'
import { Upload } from 'lucide-react'
import { useEffect, useState } from 'react'

import { usePendiente } from '../api/publicacion'
import { DialogoDePublicacion } from './DialogoDePublicacion'
import { numeroDelBoton } from './plan'

/** Lo que, si cambia, puede cambiar lo que queda por publicar: los listados, un documento, el diseño. */
const CAMBIAN_LO_PENDIENTE = new Set(['diseno-estado', 'doc', 'lista', 'versiones'])

/**
 * El número del botón se mantiene al día solo: cuando otra pantalla da por viejo un listado,
 * un documento o el estado del diseño (porque ha guardado, publicado o borrado algo), se da
 * por viejo también lo pendiente. Así el editor no tiene que saber que este botón existe.
 */
const useSeguirLoPendiente = (sitio: number) => {
  const clienteDeConsultas = useQueryClient()
  useEffect(() => {
    let temporizador: number | undefined
    const dejarDeOir = clienteDeConsultas.getQueryCache().subscribe((evento) => {
      if (evento.type !== 'updated' || evento.action.type !== 'invalidate') return
      const clave = evento.query.queryKey[0]
      if (typeof clave !== 'string' || !CAMBIAN_LO_PENDIENTE.has(clave)) return
      window.clearTimeout(temporizador)
      temporizador = window.setTimeout(() => void clienteDeConsultas.invalidateQueries({ queryKey: ['web-pendiente', sitio] }), 300)
    })
    return () => {
      window.clearTimeout(temporizador)
      dejarDeOir()
    }
  }, [clienteDeConsultas, sitio])
}

/**
 * «Publicar mi web», fijo en la cabecera del panel mientras hay una web activa, con cuántas
 * cosas le quedan por publicar. Lo ve quien publica contenido (no el autor, que publica sus
 * entradas desde su editor). En un móvil, solo el icono y el número; el nombre sigue ahí
 * para quien no ve la pantalla.
 */
export const BotonPublicarMiWeb = ({ espacio, sitio }: { espacio: number; sitio: number }) => {
  const [abierto, setAbierto] = useState(false)
  const { data } = usePendiente(sitio, { cuentas: true })
  useSeguirLoPendiente(sitio)
  const numero = numeroDelBoton(data?.total ?? 0)
  const nombre = numero ? `Publicar mi web: ${data!.total === 1 ? 'queda 1 cosa' : `quedan ${data!.total} cosas`} por publicar` : 'Publicar mi web'
  return (
    <>
      <button
        aria-haspopup="dialog"
        aria-label={nombre}
        className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-boton bg-violeta px-2.5 text-[13px] font-medium text-white shadow-suave transition-colors hover:bg-violeta-2 sm:px-3"
        onClick={() => setAbierto(true)}
        title={nombre}
        type="button"
      >
        <Upload aria-hidden className="size-4" />
        <span className="hidden sm:inline">Publicar mi web</span>
        {numero && (
          <span aria-hidden className="min-w-5 rounded-pastilla bg-papel px-1.5 text-center text-[11px] leading-5 font-semibold text-violeta tabular-nums">
            {numero}
          </span>
        )}
      </button>
      {abierto && <DialogoDePublicacion cerrar={() => setAbierto(false)} espacio={espacio} sitio={sitio} />}
    </>
  )
}
